import React, { useState, useEffect, useRef } from "react";
import {
  AppBar, Toolbar, Avatar, Typography, IconButton, Box, Menu, MenuItem,
} from "@mui/material";
import { Close, MoreVert, Search } from "@mui/icons-material";
import { useSelector } from "react-redux";
import ButtonMessage from "./ButtonMessage";
import ListMessage from "./ListMessage";
import SentMessage from "./SentMessage";
import ReceivedMessage from "./ReceivedMessage";
import ActionMessage from "./ActionMessage";
import { ListModal } from "./ListModal";

import dayjs from "dayjs";
import "dayjs/locale/es";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import weekday from "dayjs/plugin/weekday";
import localizedFormat from "dayjs/plugin/localizedFormat";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);
dayjs.extend(localizedFormat);
dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(relativeTime);
dayjs.locale("es");

// Zona horaria fija de la operación (Chile). No depende de la config del PC del agente.
const APP_TZ = "America/Santiago";

const formatTime = (timestamp) => dayjs.utc(timestamp).tz(APP_TZ).format("hh:mm a");

const formatDateLabel = (timestamp) => {
  const date = dayjs.utc(timestamp).tz(APP_TZ);
  if (date.isToday()) return "Hoy";
  if (date.isYesterday()) return "Ayer";
  const sevenDaysAgo = dayjs().subtract(7, "day");
  if (date.isAfter(sevenDaysAgo)) return date.format("dddd");
  return date.format("D [de] MMMM [de] YYYY");
};

const WhatsAppChat = ({ conversation, onClose, height = "800px", maxWidth = "480px", hideHeader = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [width, setWidth] = useState("480px");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOptions, setModalOptions] = useState([]);
  const [modalTitle, setModalTitle] = useState("");
  const [visibleDate, setVisibleDate] = useState(null);

  const messagesState = useSelector((store) => store.message.messages);
  const containerRef = useRef(null);
  const bottomRef = useRef(null);

  // Los mensajes se cargan desde la página al seleccionar la conversación.
  // Evitamos cargar por clientId para no mezclar conversaciones.

  const getTelephoneFormat = (telephone) => {
    if (!telephone) return "";
    const initialN = telephone.substring(0, 2);
    const number = telephone.substring(2);
    return `+(${initialN}) ${number}`;
  };

  const orderedMessages = Array.from(
    new Map(
      (messagesState.list || [])
        .filter(msg => msg && msg.sentAt && typeof msg === "object")
        .map(msg => [msg.id, msg])
    ).values()
  )
    .sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime())
    .map((msg, idx) => {
      let contentParsed;
      try {
        contentParsed = typeof msg.content === "string" ? JSON.parse(msg.content) : msg.content;
      } catch {
        contentParsed = msg.content;
      }

      const typeRaw = msg.messageTypeWSP?.name || "";
      let type = "sent";
      if (msg.status === "received") type = "received";
      else if (typeRaw === "action") type = "action";

      return {
        id: msg.id,
        originalIndex: idx,
        type,
        timestamp: msg.sentAt,
        formattedTime: formatTime(msg.sentAt),
        dateLabel: formatDateLabel(msg.sentAt),
        typeRaw,
        content: contentParsed,
      };
    });

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [messagesState.list]);

  const handleScroll = () => {
    const chatBox = containerRef.current;
    if (!chatBox) return;

    const items = chatBox.querySelectorAll(".message-item");
    for (const item of items) {
      const rect = item.getBoundingClientRect();
      if (rect.top > 90) {
        setVisibleDate(item.getAttribute("data-date"));
        break;
      }
    }
  };

  const handleClick = (e) => setAnchorEl(e.currentTarget);
  const handleWidth = (w) => {
    setAnchorEl(null);
    if (w) setWidth(w);
  };
  const handleOpenListModal = (title, options) => {
    setModalTitle(title);
    setModalOptions(options);
    setModalOpen(true);
  };
  const handleListSelection = () => setModalOpen(false);

  return (
    <Box sx={{ maxWidth: maxWidth, margin: hideHeader ? 0 : "auto", height: height, bgcolor: "#efeae2", display: "flex", flexDirection: "column" }}>
      {!hideHeader && (
        <AppBar position="static" sx={{ bgcolor: "#008069" }}>
          <Toolbar>
            <Avatar sx={{ width: 40, height: 40, bgcolor: "green", mr: 2 }}>D</Avatar>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              {getTelephoneFormat(conversation?.clientWSP?.whatsAppId)}
            </Typography>
            <IconButton color="inherit"><Search /></IconButton>
            <IconButton color="inherit" onClick={handleClick}><MoreVert /></IconButton>
            <IconButton color="inherit" onClick={onClose}><Close /></IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleWidth(null)}>
              <MenuItem onClick={() => handleWidth("800px")}>Tablet</MenuItem>
              <MenuItem onClick={() => handleWidth("480px")}>iPhone</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
      )}

      {visibleDate && (
        <Box sx={{
          position: "sticky",
          top: 64,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#444",
          color: "white",
          padding: "4px 12px",
          borderRadius: "10px",
          fontSize: "12px",
          zIndex: 1,
          textAlign: "center",
          width: "fit-content",
          margin: "auto",
        }}>
          {visibleDate}
        </Box>
      )}

      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{ p: 2, height: hideHeader ? "100%" : "calc(100% - 64px)", overflowY: "auto", position: "relative" }}
      >
        {orderedMessages.map((msg, index) => {
          const showDateSeparator =
            index === 0 || msg.dateLabel !== orderedMessages[index - 1].dateLabel;

          const prevMsg = index > 0 ? orderedMessages[index - 1] : null;
          const sameSenderAsPrev = prevMsg && prevMsg.type === msg.type && !showDateSeparator;
          const marginProps = { sx: { mb: sameSenderAsPrev ? '2px' : 1 } };
          const commonProps = {
            key: msg.id || index,
            timestamp: msg.timestamp,
            formattedTime: msg.formattedTime,
          };

          const content = typeof msg.content === "object"
            ? msg.content.interactive?.body?.text ||  // Estructura interactive
              msg.content.text?.body ||                // NUEVO: text.body
              (typeof msg.content.body === "object"
                ? msg.content.body.text                // body.text
                : msg.content.body)                    // body directo
            : msg.content;

          // const isButton = msg.content?.type === "button" || msg.typeRaw === "button";
          // const isList = msg.content?.type === "list" || msg.typeRaw === "list";

          // Verifica si es button (forma antigua O interactive)
          const isButton = 
            msg.content?.type === "button" || 
            msg.typeRaw === "button" ||
            (msg.content?.type === "interactive" && msg.content?.interactive?.type === "button");

          // Verifica si es list (forma antigua O interactive)
          const isList = 
            msg.content?.type === "list" || 
            msg.typeRaw === "list" ||
            (msg.content?.type === "interactive" && msg.content?.interactive?.type === "list");

          // Verifica si es Action
          const isAction = 
            msg.type === "action" || 
            msg.typeRaw === "action" ||
            (msg.content?.type === "interactive" && msg.content?.interactive?.type === "cta_url");

          return (
            <div key={index} className="message-item" data-date={msg.dateLabel}>
              {showDateSeparator && (
                <div style={{ textAlign: "center", margin: "8px 0", color: "#999" }}>
                  {msg.dateLabel}
                </div>
              )}

              <Box {...marginProps}>
                {msg.type === "sent" && isButton && (
                  <ButtonMessage
                    {...commonProps}
                    body={msg.content?.interactive?.body?.text || msg.content?.body?.text}
                    footer={msg.content?.interactive?.footer || msg.content?.footer}
                    action={msg.content?.interactive?.action || msg.content?.action}
                  />
                )}

                {msg.type === "sent" && isList && (
                  <ListMessage
                    {...commonProps}
                    body={msg.content?.interactive?.body?.text || msg.content?.body?.text}
                    action={msg.content?.interactive?.action || msg.content?.action}
                    onOpenModal={handleOpenListModal}
                    onSelect={handleListSelection}
                  />
                )}

                {msg.type === "sent" && isAction && (
                  <ActionMessage 
                    {...commonProps} 
                    content={
                      msg.content?.interactive?.body?.text || 
                      msg.content?.body?.text || 
                      content
                    }
                    buttonText={
                      msg.content?.interactive?.action?.parameters?.display_text || 
                      "Ver más"
                    }
                    url={
                      msg.content?.interactive?.action?.parameters?.url || 
                      msg.content?.url
                    }
                  />
                )}

                {msg.type === "sent" && !isButton && !isList && (
                  <SentMessage {...commonProps} content={content} />
                )}

                {msg.type === "received" && (
                  <ReceivedMessage {...commonProps} content={content} />
                )}
              </Box>
            </div>
          );
        })}
        <div ref={bottomRef}></div>
      </Box>

      <ListModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        options={modalOptions}
        onSelect={handleListSelection}
      />
    </Box>
  );
};

export default WhatsAppChat;
